import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { ContractsApi } from '@/lib/contracts-api';
import { queryKeys } from '@/config/query-keys';
import { SearchCriteriaBuilder } from '@/lib/search-criteria';
import type { CreateContractRequest } from '@/types/contract';
import type { ExtendedListParams } from '@/types/search-criteria';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const STALE_TIME = 1000 * 60 * 5;

export function useContracts(params?: ExtendedListParams) {
  const { selectedCustomer } = useAuth();
  const customerId = selectedCustomer?.customerId;

  return useQuery({
    queryKey: queryKeys.contracts.list(customerId ?? 0, params),
    queryFn: () => ContractsApi.getContracts(params),
    staleTime: STALE_TIME,
    enabled: customerId != null,
  });
}

export function useActiveContracts() {
  const criteria = SearchCriteriaBuilder
    .create('and')
    .addCondition('status', 'ne', 'closed')
    .build();

  return useContracts({ criteria, sort: ['createdAt desc'] });
}

export function useContract(contractId: number) {
  const { selectedCustomer } = useAuth();
  const customerId = selectedCustomer?.customerId;

  return useQuery({
    queryKey: queryKeys.contracts.detail(customerId ?? 0, contractId),
    queryFn: () => ContractsApi.getContractById(contractId),
    staleTime: STALE_TIME,
    enabled: !!contractId && customerId != null,
  });
}

export function useContractMutations() {
  const { t } = useTranslation();
  const { selectedCustomer } = useAuth();
  const queryClient = useQueryClient();
  const customerId = selectedCustomer?.customerId;

  const invalidateContracts = () => {
    if (customerId != null) {
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all(customerId) });
    }
  };

  const create = useMutation({
    mutationFn: (data: CreateContractRequest) => ContractsApi.createContract(data),
    onSuccess: () => {
      invalidateContracts();
      if (customerId != null) {
        queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all(customerId) });
      }
      toast.success(t('contracts.messages.createSuccess'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('contracts.errors.saveContract'));
    },
  });

  const enableRecurrent = useMutation({
    mutationFn: (contractId: number) => ContractsApi.updateContractRecurrent(contractId),
    onSuccess: () => {
      invalidateContracts();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const disableRecurrent = useMutation({
    mutationFn: (contractId: number) => ContractsApi.disableContractRecurrent(contractId),
    onSuccess: () => {
      invalidateContracts();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return { create, enableRecurrent, disableRecurrent };
}
